import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Divider,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { searchFaq } from "../../api";
import { logError } from "../../logging/loggingService";

const initialState = { loading: false, query: "", results: [], error: "" };

export default function FaqSearch({ autoFocus = false }) {
    const [state, setState] = useState(initialState);

    useEffect(() => {
        loadFaq("");
    }, []);

    const loadFaq = async (query) => {
        setState((prev) => ({ ...prev, loading: true, query }));
        try {
            const response = await searchFaq(query);
            setState((prev) => ({ ...prev, loading: false, results: response?.data ?? [], error: "" }));
        } catch (error) {
            logError("FAQ search failed", error, { component: "FaqSearch", query });
            setState((prev) => ({ ...prev, loading: false, error: "Die FAQ-Daten konnten nicht geladen werden." }));
        }
    };

    const handleChange = (event) => {
        const next = event.target.value;
        loadFaq(next);
    };

    const content = useMemo(() => {
        if (state.error) {
            return <Typography color="error">{state.error}</Typography>;
        }
        if (!state.results.length) {
            return <Typography color="text.secondary">Keine Einträge gefunden.</Typography>;
        }
        return (
            <List disablePadding>
                {state.results.map((entry) => (
                    <React.Fragment key={entry.id}>
                        <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                            <ListItemText
                                primary={entry.question}
                                primaryTypographyProps={{ component: "span", variant: "subtitle1", fontWeight: 600 }}
                                secondary={
                                    <Box component="span">
                                        <Typography
                                            variant="body2"
                                            component="span"
                                            color="text.secondary"
                                            sx={{ display: "block", whiteSpace: "pre-line" }}
                                        >
                                            {entry.answer}
                                        </Typography>
                                        {entry.tags?.length ? (
                                            <Typography
                                                variant="caption"
                                                component="span"
                                                color="primary"
                                                sx={{ mt: 0.5, display: "block" }}
                                            >
                                                {entry.tags.join(" · ")}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                }
                            />
                        </ListItem>
                        <Divider component="li" />
                    </React.Fragment>
                ))}
            </List>
        );
    }, [state.results, state.error]);

    return (
        <Card>
            <CardHeader title="FAQ & Hilfe" subheader="Suchen Sie nach Antworten auf häufige Fragen" />
            <CardContent>
                <TextField
                    fullWidth
                    autoFocus={autoFocus}
                    placeholder="z. B. Push-Benachrichtigungen"
                    variant="outlined"
                    value={state.query}
                    onChange={handleChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ mt: 2, maxHeight: 260, overflowY: "auto" }}>{content}</Box>
            </CardContent>
        </Card>
    );
}

